// SPDX-License-Identifier: Elastic-2.0
/**
 * Service-worker teardown (issue #532).
 *
 * The Mirlai appliance is a kiosk on a LOCAL, always-on gateway. A service
 * worker bought us nothing there and actively broke things: a previously
 * shipped Workbox SW sat between the PWA and the gateway and FAILED the
 * (same-origin) WiFi-scan fetch in WPEWebKit — the request never reached the
 * gateway and the onboarding sat on a perpetual "Scanning…" / "Load failed".
 * SW asset caching also meant new add-on builds never took effect until a
 * stale-cache reload.
 *
 * Rather than register a SW, this module now PURGES any service worker (and
 * all Cache Storage) previously installed on the device, then reloads once so
 * the now-uncontrolled page fetches the gateway directly. This is page-side —
 * it works regardless of the old SW's internal state (no update/skipWaiting
 * deadlock to fight). No SW is registered going forward.
 *
 * (PWA installability for the future phone app can be revisited separately;
 * the kiosk does not need it.)
 */

const PURGE_FLAG = "mirlai-sw-purged";

export interface RegisterSWOptions {
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
}

/** Unregister every service worker + delete every cache, then reload once. */
export async function registerSW(_opts: RegisterSWOptions = {}): Promise<void> {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    const hadController = navigator.serviceWorker.controller !== null;

    if (regs.length === 0 && !hadController) {
      // Already clean — nothing to tear down.
      return;
    }

    await Promise.all(regs.map((r) => r.unregister().catch(() => false)));

    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }

    // The current document is still controlled by the (now-unregistered) SW
    // until a navigation, so reload once to drop it. Guard with a session
    // flag so we never loop.
    if (!sessionStorage.getItem(PURGE_FLAG)) {
      sessionStorage.setItem(PURGE_FLAG, "1");
      window.location.reload();
    }
  } catch {
    // Best-effort cleanup — never block app start on it.
  }
}

/** Retained for API compatibility (the update-toast path). No SW now. */
export async function reloadForUpdate(): Promise<void> {
  window.location.reload();
}
